import * as React from "react"
import { Minus, Plus } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface NumberInputProps {
  value?: number
  defaultValue?: number
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  onChange?: (value: number) => void
  className?: string
  id?: string
  name?: string
  "aria-label"?: string
  "data-testid"?: string
}

/**
 * Numeric input with min/max clamping and stepper affordances.
 *
 * @category Forms
 */
function NumberInput({
  value: controlledValue,
  defaultValue = 0,
  min = 0,
  max = Infinity,
  step = 1,
  disabled = false,
  onChange,
  className,
  id,
  name,
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
}: NumberInputProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue)
  const value = controlledValue ?? internalValue

  const setValue = React.useCallback(
    (next: number) => {
      const clamped = Math.min(max, Math.max(min, next))
      setInternalValue(clamped)
      onChange?.(clamped)
    },
    [min, max, onChange]
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseFloat(e.target.value)
    if (!isNaN(parsed)) {
      setValue(parsed)
    }
  }

  return (
    <div className={cn("flex items-center", className)}>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-10 w-10 rounded-r-none border-r-0"
        disabled={disabled || value <= min}
        onClick={() => setValue(value - step)}
        aria-label="Decrease"
      >
        <Minus className="h-4 w-4" />
      </Button>
      <Input
        type="number"
        id={id}
        name={name}
        value={value}
        onChange={handleInputChange}
        disabled={disabled}
        className="h-10 w-16 rounded-none text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        aria-label={ariaLabel}
        data-testid={dataTestId}
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-10 w-10 rounded-l-none border-l-0"
        disabled={disabled || value >= max}
        onClick={() => setValue(value + step)}
        aria-label="Increase"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  )
}

export { NumberInput }
export type { NumberInputProps }
